<template>
  <AdminLayout>
    <div class="emergencies-history">
      <div class="history-container">
        <!-- Header Section -->
        <div class="history-header">
          <div class="header-content">
            <h1 class="page-title">
              <i class="bi bi-clock-history"></i>
              Emergency Requests History
            </h1>
            <div class="header-stats">
              <div class="stat-badge">
                <span class="stat-number">{{ totalRows }}</span>
                <span class="stat-label">Total Records</span>
              </div>
            </div>
          </div>
          <p class="page-subtitle">
            Search and review previous emergency reports and requests
          </p>
        </div>

        <!-- Search Section -->
        <form @submit.prevent="search">
          <!-- Results Section -->
          <div class="results-section">
            <div class="results-card">
              <div class="table-responsive">
                <table class="emergencies-table">
                  <thead>
                    <tr>
                      <td colspan="7">
                        <div class="d-flex justify-content-between mb-0 mt-3">
                          <h3>
                            <i class="bi bi-search"></i>
                            Search Filters
                          </h3>
                          <button
                            @click="search"
                            class="search-btn"
                            :disabled="isSearching"
                          >
                            <i
                              class="bi"
                              :class="isSearching ? 'bi-arrow-repeat spin' : 'bi-search'"
                            ></i>
                            {{ isSearching ? "Searching..." : "Search" }}
                          </button>
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <th class="name-col">Caller Name</th>
                      <th class="type-col">Emergency Type</th>
                      <th class="location-col">Location</th>
                      <th class="date-col">Date & Time</th>
                      <th class="status-col">Status</th>
                      <th class="operator-col">Operator</th>
                    </tr>
                    <tr style="cursor: pointer">
                      <th>
                        <input class="form-control" v-model="searchModel.callerName" />
                      </th>
                      <th>
                        <input class="form-control" v-model="searchModel.emergencyType" />
                      </th>
                      <th>
                        <input class="form-control" v-model="searchModel.location" />
                      </th>
                      <th>
                        <input
                          class="form-control"
                          v-model="searchModel.createdAtString"
                        />
                      </th>
                      <th>
                        <input class="form-control" v-model="searchModel.status" />
                      </th>
                      <th>
                        <input class="form-control" v-model="searchModel.operatorName" />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <!-- Skeleton Loading -->

                    <template v-if="isSearching">
                      <tr v-for="n in 6" :key="`skeleton-${n}`" class="skeleton-row">
                        <td>
                          <div class="skeleton skeleton-text"></div>
                        </td>
                        <td>
                          <div class="skeleton skeleton-text"></div>
                        </td>
                        <td>
                          <div class="skeleton skeleton-text"></div>
                        </td>
                        <td>
                          <div class="skeleton skeleton-text"></div>
                        </td>
                        <td>
                          <div class="skeleton skeleton-badge"></div>
                        </td>
                        <td>
                          <div class="skeleton skeleton-text"></div>
                        </td>
                      </tr>
                    </template>
                    <!-- Empty State -->
                    <tr v-if="emergencies.length === 0 && !isSearching">
                      <td colspan="6" class="empty-state">
                        <div class="empty-content">
                          <i class="bi bi-inbox"></i>
                          <h3>No records found</h3>
                          <p>
                            Try adjusting your search criteria or clear filters to see all
                            records.
                          </p>
                        </div>
                      </td>
                    </tr>

                    <!-- Results -->
                    <tr
                      v-else
                      v-for="emergency in emergencies"
                      :key="emergency.erId"
                      class="emergency-row"
                      @click="handleRequestClick(emergency.erId)"
                    >
                      <td class="name-cell">
                        <div class="caller-info">
                          <div class="caller-name">
                            {{ emergency.callerName || "Unknown" }}
                          </div>
                        </div>
                      </td>
                      <td class="type-cell">
                        <span
                          class="emergency-type-badge"
                          :class="getEmergencyTypeClass(emergency.emergencyType)"
                        >
                          {{ emergency.emergencyType }}
                        </span>
                      </td>
                      <td class="location-cell">
                        <div class="location-info">
                          <i class="bi bi-geo-alt"></i>
                          {{ emergency.location || "Unknown location" }}
                        </div>
                      </td>
                      <td class="date-cell">
                        <div class="datetime-info">
                          <div class="date">
                            {{ formatDate(emergency.createdAtString) }}
                          </div>
                          <div class="time">
                            {{ formatTime(emergency.createdAtString) }}
                          </div>
                        </div>
                      </td>
                      <td class="status-cell">
                        <span
                          class="status-badge"
                          :class="getStatusClass(emergency.status)"
                        >
                          {{ emergency.status }}
                        </span>
                      </td>
                      <td class="operator-cell">
                        <div class="operator-info">
                          <i class="bi bi-person-gear"></i>
                          {{ emergency.operatorName || "Unassigned" }}
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Results Footer -->
              <div class="table-footer" v-if="emergencies.length > 0 && !isSearching">
                <div class="results-count">
                  Showing {{ emergencies.length }} of {{ totalRows }} records
                </div>
              </div>
            </div>
          </div>
          <button type="submit" class="d-none"></button>
        </form>
      </div>
    </div>
    <FullscreenModal :show="showModal" @close="toggleModal">
      <EmergencyRequestInfo :id="currentId" />
    </FullscreenModal>
  </AdminLayout>
</template>

<script setup>
import FullscreenModal from "@/components/modals/FullscreenModal.vue";
import AdminLayout from "@/layouts/AdminLayout.vue";
import emergencyService from "@/services/emergencyService";
import EmergencyRequestInfo from "@/components/infos/EmergencyRequestInfo.vue";
import { ref, onMounted } from "vue";
const currentId = ref(null);
const showModal = ref(false);
const toggleModal = () => {
  showModal.value = !showModal.value;
};

const searchModel = ref({
  callerName: "",
  emergencyType: "",
  location: "",
  createdAtString: "",
  status: "",
  operatorName: "",
});

const handleRequestClick = (id) => {
  currentId.value = id;
  toggleModal();
};

const isSearching = ref(false);
const emergencies = ref([]);
const totalRows = ref(0);

const search = async () => {
  isSearching.value = true;
  try {
    const resp = await emergencyService.search(searchModel.value);
    emergencies.value = resp.emergencies;
    totalRows.value = resp.totalRows;
  } catch (error) {
    console.error("Search failed:", error);
    emergencies.value = [];
    totalRows.value = 0;
  } finally {
    isSearching.value = false;
  }
};
// eslint-disable-next-line
const getEmergencyTypeClass = (type) => {
  // eslint-disable-next-line
  const typeMap = {
    medical: "medical",
    fire: "fire",
    police: "police",
    accident: "accident",
    rescue: "rescue",
  };
  return "default"; //typeMap[type?.toLowerCase()] || "default";
};

const getStatusClass = (status) => {
  const statusMap = {
    completed: "completed",
    "in progress": "in-progress",
    pending: "pending",
    cancelled: "cancelled",
    dispatched: "dispatched",
  };
  return statusMap[status?.toLowerCase()] || "default";
};

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  return dateString.split(" ")[0]; // Return MM-DD-YYYY part
};

const formatTime = (dateString) => {
  if (!dateString) return "N/A";
  const parts = dateString.split(" ");
  return parts.length > 1 ? `${parts[1]} ${parts[2]}` : ""; // Return time and AM/PM
};

// Load initial data
onMounted(() => {
  search();
});
</script>

<style scoped>
.emergencies-history {
  min-height: 100vh;
  background: linear-gradient(135deg, #a30e0e 0%, #8a0b0b 100%);
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
  padding: 1rem;
}

.history-container {
  max-width: 1400px;
  margin: 0 auto;
}

/* Header Styles */
.history-header {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 16px;
  padding: 2rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.page-title {
  color: #2d3748;
  font-size: 2rem;
  font-weight: 700;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.page-title i {
  color: #a30e0e;
}

.page-subtitle {
  color: #718096;
  font-size: 1.1rem;
  margin: 0;
}

.header-stats {
  display: flex;
  gap: 1rem;
}

.stat-badge {
  background: linear-gradient(135deg, #a30e0e, #8a0b0b);
  color: white;
  padding: 1rem 1.5rem;
  border-radius: 12px;
  text-align: center;
  min-width: 120px;
}

.stat-number {
  display: block;
  font-size: 1.8rem;
  font-weight: 700;
  line-height: 1;
}

.stat-label {
  font-size: 0.8rem;
  opacity: 0.9;
  margin-top: 0.25rem;
}

/* Search Section */
.search-section {
  margin-bottom: 1.5rem;
}

.search-card {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
}

.search-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #e2e8f0;
}

.search-header h3 {
  margin: 0;
  color: #2d3748;
  font-size: 1.3rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.search-btn {
  background: linear-gradient(135deg, #a30e0e, #8a0b0b);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.search-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(163, 14, 14, 0.3);
}

.search-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.filter-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.filter-group {
  display: flex;
  flex-direction: column;
}

.filter-group label {
  font-weight: 600;
  color: #4a5568;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
}

.form-control {
  border: 2px solid #e2e8f0;
  padding: 0.25rem;
  font-size: 0.9rem;
  transition: all 0.3s ease;
}

.form-control:focus {
  outline: none;
  border-color: #a30e0e;
  box-shadow: 0 0 0 3px rgba(163, 14, 14, 0.1);
}

/* Results Section */
.results-card {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
}

.emergencies-table {
  width: 100%;
  border-collapse: collapse;
}

.emergencies-table th {
  background: #f7fafc;
  padding: 1rem;
  font-weight: 600;
  color: #4a5568;
  text-align: left;
  border-bottom: 2px solid #e2e8f0;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.emergencies-table td {
  padding: 1rem;
  border-bottom: 1px solid #e2e8f0;
  vertical-align: middle;
}

.emergency-row:hover {
  background: #f8f9fa;
  cursor: pointer;
}

/* Badge Styles */
.emergency-type-badge,
.status-badge {
  padding: 0.4rem 0.8rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: capitalize;
}

.emergency-type-badge.medical {
  background: #fed7d7;
  color: #c53030;
}
.emergency-type-badge.fire {
  background: #fed7d7;
  color: #dd6b20;
}
.emergency-type-badge.police {
  background: #bee3f8;
  color: #2b6cb0;
}
.emergency-type-badge.accident {
  background: #fefcbf;
  color: #d69e2e;
}
.emergency-type-badge.rescue {
  background: #c6f6d5;
  color: #276749;
}
.emergency-type-badge.default {
  background: #e2e8f0;
  color: #4a5568;
}

.status-badge.completed {
  background: #c6f6d5;
  color: #276749;
}
.status-badge.in-progress {
  background: #bee3f8;
  color: #2b6cb0;
}
.status-badge.pending {
  background: #fefcbf;
  color: #d69e2e;
}
.status-badge.cancelled {
  background: #fed7d7;
  color: #c53030;
}
.status-badge.dispatched {
  background: #e9d8fd;
  color: #6b46c1;
}
.status-badge.default {
  background: #e2e8f0;
  color: #4a5568;
}

/* Cell Styles */
.caller-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.caller-name {
  font-weight: 600;
  color: #2d3748;
}

.location-info,
.operator-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #4a5568;
}

.datetime-info {
  display: flex;
  flex-direction: column;
}

.date {
  font-weight: 600;
  color: #2d3748;
  font-size: 0.9rem;
}

.time {
  color: #718096;
  font-size: 0.8rem;
}

/* Skeleton Loading */
.skeleton-row td {
  padding: 1rem;
}

.skeleton {
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;
}

.skeleton-text {
  height: 12px;
  width: 80%;
}

.skeleton-badge {
  height: 24px;
  width: 60px;
}

@keyframes loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.spin {
  animation: spin 1s linear infinite;
}

/* Empty State */
.empty-state {
  padding: 4rem 2rem;
  text-align: center;
}

.empty-content {
  color: #718096;
}

.empty-content i {
  font-size: 4rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}

.empty-content h3 {
  margin: 0 0 0.5rem 0;
  color: #4a5568;
}

.empty-content p {
  margin: 0;
  font-size: 0.9rem;
}

/* Table Footer */
.table-footer {
  padding: 1rem;
  background: #f7fafc;
  border-top: 1px solid #e2e8f0;
}

.results-count {
  color: #718096;
  font-size: 0.9rem;
  text-align: center;
}

/* Responsive Design */
@media (max-width: 768px) {
  .history-header {
    padding: 1.5rem;
  }

  .header-content {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }

  .page-title {
    font-size: 1.5rem;
  }

  .filter-grid {
    grid-template-columns: 1fr;
  }

  .search-header {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }

  .emergencies-table {
    font-size: 0.8rem;
  }

  .emergencies-table th,
  .emergencies-table td {
    padding: 0.75rem 0.5rem;
  }
}
</style>
